/* eslint-disable unicode-bom */
import { api } from "./api";
import {
  RankingPlayer,
  Ranking,
  UpdateRankingPayload,
  RankingMetric,
  RankingParticipant,
  RankingAward,
  LeaderboardPayload,
  LeaderboardResponse,
  RankingPeriod,
  RankingVisualMode,
  Team,
} from "types/ranking";
import { User } from "types/user";
import { userToRankingPlayer } from "utils/ranking-helpers";
import type { CreateRankingPayload } from "types/ranking";

// ============================================
// FUNÇÕES ORIGINAIS (PLAYER STATUS)
// ============================================
export async function getPlayerStatus(playerId?: string): Promise<User> {
  const endpoint = playerId ? `/player/${playerId}/status` : "/player/status";
  const response = await api.get(endpoint);
  return response.data;
}

export async function getRanking(options?: {
  period?: "daily" | "weekly" | "monthly" | "all-time";
  category?: string;
  limit?: number;
}): Promise<RankingPlayer[]> {
  const response = await api.get("/ranking", { params: options });
  const players: User[] = response.data;
  return players.map((player, index) => userToRankingPlayer(player, index + 1));
}

export async function getPlayerPosition(playerId: string): Promise<number> {
  try {
    const response = await api.get(`/player/${playerId}/position`);
    return response.data.position;
  } catch (error) {
    return 0;
  }
}

export async function getPlayerRankingData(
  playerId?: string
): Promise<RankingPlayer> {
  const [status, position] = await Promise.all([
    getPlayerStatus(playerId),
    playerId ? getPlayerPosition(playerId) : Promise.resolve(0),
  ]);
  return userToRankingPlayer(status, position);
}

export async function getTopPlayers(limit = 5): Promise<RankingPlayer[]> {
  try {
    const allPlayers = await getRanking({ limit });
    return allPlayers.slice(0, limit);
  } catch (error) {
    return [];
  }
}

// ============================================
// GESTÃO DE RANKINGS (CONFORME STUDIO)
// ============================================

/**
 * Métricas disponíveis (conforme Studio)
 */
export async function getAvailableMetrics(): Promise<RankingMetric[]> {
  // Retorna métricas hardcoded como no Studio
  return [
    { id: "points", label: "Pontos" },
    { id: "tasks", label: "Tarefas" },
  ];
}

/*
 * Busca tipos de pontos disponíveis na API
 * Endpoint: GET /v3/point
 */
export async function getAvailablePoints(): Promise<any[]> {
  try {
    const response = await api.get("/point");
    return response.data || [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Busca ações disponíveis na API
 * Endpoint: GET /v3/action
 */
export async function getAvailableActions(): Promise<any[]> {
  try {
    const response = await api.get("/action");
    return response.data || [];
  } catch (error: any) {
    return [];
  }
}

/**
 * Busca todos os prêmios disponíveis
 * Endpoint: GET /v3/database/premio__c
 */
export async function getAvailableAwards(): Promise<RankingAward[]> {
  try {
    const response = await api.get("/database/premio__c", {
      headers: { Range: "items=0-100" },
    });

    if (!response.data || !Array.isArray(response.data)) {
      return [];
    }

    return response.data.map((award: any) => ({
      _id: award._id,
      name: award.name || award.nome || "Prêmio sem nome",
      image: award.image || award.imagem,
      description: award.description || award.descricao || "",
      created: award.created,
      updated: award.updated,
    }));
  } catch (error: any) {
    console.error("Erro ao buscar prêmios:", error);
    return [];
  }
}

/**
 * Busca um prêmio específico por ID
 * Endpoint: GET /v3/database/premio__c?strict=true&q=_id:'...'
 */
export async function getAwardById(
  awardId: string
): Promise<RankingAward | null> {
  try {
    const response = await api.get(
      `/database/premio__c?strict=true&q=_id:'${awardId}'`
    );

    if (!Array.isArray(response.data) || response.data.length === 0) {
      return null;
    }

    const award = response.data[0];
    return {
      _id: award._id,
      name: award.name || award.nome || "Prêmio sem nome",
      image: award.image || award.imagem,
      description: award.description || award.descricao || "",
      created: award.created,
      updated: award.updated,
    };
  } catch (error: any) {
    console.error("Erro ao buscar prêmio:", error);
    return null;
  }
}

/**
 * Cria um novo prêmio
 * Endpoint: PUT /v3/database/premio__c
 * Usa PUT (não POST) conforme o padrão do Studio
 */
export async function createAward(payload: {
  name: string;
  image?: string;
  description?: string;
}): Promise<RankingAward> {
  const generatedId = payload.name.toLowerCase().replace(/[^a-z0-9]+/g, "_");

  // Constrói o objeto apenas com campos que têm valor (evita undefined)
  const dataToSave: any = {
    _id: generatedId,
    name: payload.name,
    created: { $date: new Date().getTime() },
    updated: { $date: new Date().getTime() },
  };

  if (payload.image) {
    dataToSave.image = payload.image;
  }

  if (payload.description) {
    dataToSave.description = payload.description;
  }

  const response = await api.put("/database/premio__c", dataToSave);
  return response.data || dataToSave;
}

/**
 * Atualiza um prêmio existente
 * Endpoint: PUT /v3/database/premio__c
 */
export async function updateAward(
  awardId: string,
  payload: {
    name?: string;
    image?: string;
    description?: string;
  }
): Promise<RankingAward> {
  const dataToUpdate: any = {
    _id: awardId,
    ...payload,
    updated: { $date: new Date().getTime() },
  };

  const response = await api.put("/database/premio__c", dataToUpdate);
  return response.data;
}

/**
 * Deleta um prêmio
 * Endpoint: DELETE /v3/database/premio__c?q=_id:'...'
 */
export async function deleteAward(awardId: string): Promise<void> {
  await api.delete(`/database/premio__c?q=_id:'${awardId}'`);
}

/**
 * Busca todos os participantes da collection participantes__c
 * Endpoint: /v3/database/participantes__c
 */
export async function getAllPlayers(): Promise<RankingParticipant[]> {
  try {
    const response = await api.get("/database/participantes__c", {
      headers: { Range: "items=0-1000" },
    });

    if (!response.data || !Array.isArray(response.data)) {
      return [];
    }

    return response.data.map((player: any) => ({
      _id: player._id,
      name: player.name || player.nome || "Sem nome",
      email: player.email || "",
      avatar: player.image || player.avatar,
      fotoDePerfil: player.fotoDePerfil || player.foto_de_perfil,
    }));
  } catch (error: any) {
    return [];
  }
}

/**
 * Cria um novo ranking no Studio
 * Endpoint: PUT /v3/database/configuracao_de_ranking__c
 * Usa PUT (não POST) conforme o Studio
 */
export async function createRanking(
  payload: CreateRankingPayload
): Promise<Ranking> {
  const generatedId = payload.title.toLowerCase().replace(/[^a-z0-9]+/g, "_");

  const dataToSave: Partial<Ranking> = {
    ...payload,
    _id: generatedId,
    created: { $date: new Date().getTime() },
    updated: { $date: new Date().getTime() },
  } as any;

  const response = await api.put(
    "/database/configuracao_de_ranking__c",
    dataToSave
  );

  return response.data;
}

/**
 * Atualiza um ranking existente
 * Endpoint: PUT /v3/database/configuracao_de_ranking__c
 */
export async function updateRanking(
  rankingId: string,
  payload: UpdateRankingPayload
): Promise<Ranking> {
  const dataToUpdate: any = {
    ...payload,
    _id: rankingId,
    updated: { $date: new Date().getTime() },
  };

  const response = await api.put(
    "/database/configuracao_de_ranking__c",
    dataToUpdate
  );
  return response.data;
}

/**
 * Atualiza APENAS os visuals de um ranking (modo exibição)
 * Atualiza tanto no Studio quanto no Leaderboard (campo extra.visuals)
 * Mantém todos os outros campos intactos (title, metric, period, participants)
 * @param rankingId - ID do ranking
 * @param visuals - Array de modos visuais
 */
export async function updateRankingVisuals(
  rankingId: string,
  visuals: RankingVisualMode[]
): Promise<{ ranking: Ranking; leaderboard?: LeaderboardResponse }> {
  try {
    // 1. Buscar ranking completo e leaderboards em paralelo
    const [currentRanking, allLeaderboards] = await Promise.all([
      getRankingById(rankingId),
      getAllLeaderboards(),
    ]);

    // 2. Atualizar visuals no Studio (PUT com TODOS os campos)
    const dataToUpdate: Ranking = {
      ...currentRanking,
      visuals: visuals,
      updated: { $date: new Date().getTime() },
    };

    const studioResponse = await api.put(
      "/database/configuracao_de_ranking__c",
      dataToUpdate
    );

    const updatedRanking = studioResponse.data;

    // 3. Buscar leaderboard correspondente
    const leaderboard = allLeaderboards.find(
      (lb) => lb.extra?._id === rankingId || lb.title === currentRanking.title
    );

    if (leaderboard) {
      // 4. Atualizar leaderboard com novo ranking no campo extra
      const rankingWithNewVisuals: Ranking = {
        ...currentRanking,
        visuals: visuals,
        updated: { $date: new Date().getTime() },
      };

      const updatedLeaderboard = await updateLeaderboard(
        leaderboard._id,
        rankingWithNewVisuals
      );

      return { ranking: updatedRanking, leaderboard: updatedLeaderboard };
    }

    return { ranking: updatedRanking };
  } catch (error: any) {
    throw error;
  }
}

/**
 * Atualiza um leaderboard existente (inclui configuração no campo extra)
 * Busca o leaderboard atual e mantém todos os campos, atualizando apenas o extra
 * A API do Leaderboard usa POST (não PUT) para updates
 * Endpoint: POST /v3/leaderboard (com _id no payload para update)
 * @param leaderboardId - ID do leaderboard
 * @param ranking - Dados completos do ranking (vai no campo extra)
 */
export async function updateLeaderboard(
  leaderboardId: string,
  ranking: Ranking
): Promise<LeaderboardResponse> {
  try {
    // 1. Buscar leaderboard atual para preservar todos os campos
    const allLeaderboards = await getAllLeaderboards();
    const currentLeaderboard = allLeaderboards.find(
      (lb) => lb._id === leaderboardId
    );

    if (!currentLeaderboard) {
      throw new Error(`Leaderboard ${leaderboardId} não encontrado`);
    }

    // 2. Manter TODOS os campos do leaderboard atual, apenas atualizando o extra
    const leaderboardData: any = {
      _id: leaderboardId,
      title: currentLeaderboard.title,
      description: currentLeaderboard.description,
      principalType: currentLeaderboard.principalType,
      operation: currentLeaderboard.operation,
      period: currentLeaderboard.period,
      principals: currentLeaderboard.principals,
      techniques: currentLeaderboard.techniques,
      extra: ranking,
    };

    const response = await api.post("/leaderboard", leaderboardData);
    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Atualiza ranking completo (Studio + Leaderboard)
 * 1. Atualiza configuração no Studio
 * 2. Busca leaderboard correspondente
 * 3. Atualiza leaderboard com nova configuração no campo extra
 */
export async function updateFullRanking(
  rankingId: string,
  payload: UpdateRankingPayload
): Promise<{ ranking: Ranking; leaderboard?: LeaderboardResponse }> {
  try {
    // 1. Atualizar configuração no Studio e buscar leaderboards em paralelo
    const [updatedRanking, allLeaderboards] = await Promise.all([
      updateRanking(rankingId, payload),
      getAllLeaderboards(),
    ]);

    // 2. Buscar leaderboard correspondente
    const leaderboard = allLeaderboards.find(
      (lb) => lb.extra?._id === rankingId || lb.title === updatedRanking.title
    );

    if (leaderboard) {
      // 3. Atualizar leaderboard com configuração atualizada
      const updatedLeaderboard = await updateLeaderboard(
        leaderboard._id,
        updatedRanking
      );

      // Limpa o cache após atualizar
      clearLeaderboardsCache();

      return { ranking: updatedRanking, leaderboard: updatedLeaderboard };
    }

    // Limpa o cache mesmo que não tenha leaderboard
    clearLeaderboardsCache();

    return { ranking: updatedRanking };
  } catch (error: any) {
    throw error;
  }
}

/**
 * Busca um ranking específico por ID
 * Endpoint: GET /v3/database/configuracao_de_ranking__c?strict=true&q=_id:'...'
 */
export async function getRankingById(rankingId: string): Promise<Ranking> {
  try {
    const response = await api.get(
      `/database/configuracao_de_ranking__c?strict=true&q=_id:'${rankingId}'`
    );

    if (!Array.isArray(response.data) || response.data.length === 0) {
      throw new Error("Ranking não encontrado");
    }

    const ranking = response.data[0];

    // Migrar campo legado visual -> visuals (compatibilidade)
    if ((ranking as any).visual && !ranking.visuals) {
      ranking.visuals = [(ranking as any).visual];
      delete (ranking as any).visual;
    }

    if (!ranking.visuals) ranking.visuals = ["podio"];
    if (!ranking.participants) ranking.participants = [];

    return ranking;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Busca todos os rankings
 * Endpoint: GET /database/configuracao_de_ranking__c
 */
export async function getAllRankings(): Promise<Ranking[]> {
  try {
    const response = await api.get("/database/configuracao_de_ranking__c", {
      headers: { Range: "items=0-100" },
    });

    if (!response.data || !Array.isArray(response.data)) {
      return [];
    }

    const rankings = response.data.map((ranking: any) => {
      // Migrar campo legado visual -> visuals
      if (ranking.visual && !ranking.visuals) {
        ranking.visuals = [ranking.visual];
        delete ranking.visual;
      }

      if (!ranking.visuals) ranking.visuals = ["podio"];
      if (!ranking.participants) ranking.participants = [];

      return ranking;
    });

    return rankings;
  } catch (error: any) {
    return [];
  }
}

/**
 * Deleta um ranking
 * Endpoint: DELETE /database/configuracao_de_ranking__c?q=_id:'...'
 */
export async function deleteRanking(rankingId: string): Promise<void> {
  await api.delete(`/database/configuracao_de_ranking__c?q=_id:'${rankingId}'`);
  clearLeaderboardsCache();
}

// ============================================
// FUNÇÕES PARA JOGADORES (EXIBIÇÃO DE RANKINGS)
// ============================================

/**
 * Busca todos os rankings onde o jogador é participante
 * @param playerEmail Email do jogador (usado como ID na collection participantes)
 */
export async function getPlayerRankings(
  playerEmail: string
): Promise<Ranking[]> {
  try {
    const allRankings = await getAllRankings();
    const playerRankings = allRankings.filter((ranking) =>
      ranking.participants.includes(playerEmail)
    );
    return playerRankings;
  } catch (error: any) {
    return [];
  }
}

/**
 * Busca os dados de um ranking específico com os jogadores ordenados por pontos
 * Tenta buscar dados reais do Leaderboard API, se falhar usa dados mock
 * @param rankingId ID do ranking (configuracao_de_ranking__c._id)
 */
export async function getRankingWithPlayers(
  rankingId: string
): Promise<RankingPlayer[]> {
  try {
    const ranking = await getRankingById(rankingId);
    const allLeaderboards = await getAllLeaderboards();

    const leaderboard = allLeaderboards.find(
      (lb) => lb.extra?._id === rankingId || lb.title === ranking.title
    );

    if (leaderboard) {
      try {
        const leaders = await getLeaderboardLeaders(leaderboard._id);

        if (leaders && leaders.length > 0) {
          const players: RankingPlayer[] = leaders.map((leader, index) => ({
            id: leader.player || leader._id,
            name: leader.name || "Sem nome",
            avatar: leader.image,
            fotoDePerfil: leader.image,
            position: leader.position || index + 1,
            points: leader.total || 0,
            maxPoints: Math.max(...leaders.map((l) => l.total || 0)),
            level: Math.floor((leader.total || 0) / 200) + 1,
            progress: Math.floor(((leader.total || 0) % 200) / 2),
          }));

          return players;
        }
      } catch (leaderError) {
        // Fallback para mock em caso de erro
      }
    }

    // FALLBACK: usar participantes mock
    const allParticipants = await getAllPlayers();

    const rankingParticipants = allParticipants.filter(
      (p) =>
        ranking.participants.includes(p._id) ||
        ranking.participants.includes(p.email || "") ||
        ranking.participants.includes(p.name || "")
    );

    const players: RankingPlayer[] = rankingParticipants.map((p, index) => ({
      id: p._id,
      name: p.name || p.nome || p.email || "Sem nome",
      avatar: p.avatar || p.image,
      fotoDePerfil: p.fotoDePerfil || p.foto_de_perfil,
      position: index + 1,
      points: Math.floor(Math.random() * 3000) + 500,
      maxPoints: 3000,
      level: Math.floor(Math.random() * 15) + 1,
      progress: Math.floor(Math.random() * 100),
    }));

    players.sort((a, b) => b.points - a.points);
    players.forEach((player, index) => {
      player.position = index + 1;
    });

    return players;
  } catch (error: any) {
    return [];
  }
}

// ============================================
// LEADERBOARD API (/v3/leaderboard)
// ============================================

// Cache simples para evitar requisições desnecessárias
let leaderboardsCache: {
  data: LeaderboardResponse[] | null;
  timestamp: number;
} = {
  data: null,
  timestamp: 0,
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Limpa o cache de leaderboards
 * Útil após criar/atualizar/deletar rankings
 */
export function clearLeaderboardsCache(): void {
  leaderboardsCache = { data: null, timestamp: 0 };
  playerLeaderboardsCache.clear();
  leadersCache.clear();
  pendingPlayerLeaderboardRequests.clear();
  pendingLeaderRequests.clear();
}

/**
 * Mapeia o período do nosso formato para o formato da API Leaderboard
 */
function mapPeriodToLeaderboard(periodParam: any): {
  type: number;
  timeAmount: number;
  timeScale: number;
} {
  // If periodParam already looks like a LeaderboardPeriod, return it
  if (periodParam && typeof periodParam === "object" && "type" in periodParam) {
    return periodParam as {
      type: number;
      timeAmount: number;
      timeScale: number;
    };
  }

  const period: RankingPeriod = periodParam as RankingPeriod;

  const periodMap: Record<
    RankingPeriod,
    { type: number; timeAmount: number; timeScale: number }
  > = {
    semanal: { type: 0, timeAmount: 1, timeScale: 5 }, // 5 = semanal
    mensal: { type: 0, timeAmount: 1, timeScale: 6 }, // 6 = mensal
    anual: { type: 0, timeAmount: 1, timeScale: 7 }, // 7 = anual
  };

  return periodMap[period];
}

/**
 * Mapeia a métrica do nosso formato para o formato da API Leaderboard
 * @param metric - Tipo de métrica (points ou tasks)
 * @param actionId - ID da ação (obrigatório se metric === 'tasks')
 * @param pointId - ID do tipo de ponto (obrigatório se metric === 'points')
 */
function mapMetricToLeaderboard(
  metric: string,
  actionId?: string,
  pointId?: string
): string {
  if (metric === "tasks" && actionId) {
    return actionId; // Retorna o ID da ação selecionada
  }

  if (metric === "points" && pointId) {
    return pointId; // Retorna o ID do tipo de ponto selecionado
  }

  // Fallback para XP
  return "xp";
}

/**
 * Cria um leaderboard na API /v3/leaderboard
 * Esta requisição é feita em paralelo com createRanking()
 * @param payload - Dados do ranking (deve conter metric, actionType/pointType, period, participants)
 * @returns LeaderboardResponse com _id gerado pela API
 */
export async function createLeaderboard(
  payload: any
): Promise<LeaderboardResponse> {
  try {
    const operationType = payload.metric === "tasks" ? 1 : 3;

    const leaderboardData: LeaderboardPayload = {
      title: payload.title,
      description: payload.title,
      principalType: 0,
      operation: {
        type: operationType,
        achievement_type: 0,
        item: mapMetricToLeaderboard(
          payload?.metric || "points",
          payload?.actionType,
          payload?.pointType
        ),
        filters: [],
        sort: -1,
        sub: false,
      },
      period: mapPeriodToLeaderboard(payload?.period || "semanal"),
      principals: payload?.participants || [],
      techniques: ["GT03"],
    };

    const response = await api.post("/leaderboard", leaderboardData);
    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Cria ranking completo (configuração + leaderboard)
 * 1. Cria configuração no Studio (/database/configuracao_de_ranking__c)
 * 2. Usa resposta do Studio no campo 'extra' do Leaderboard
 * 3. Cria Leaderboard na API (/v3/leaderboard)
 * @param payload - Dados do ranking incluindo metric, actionType/pointType, period, participants
 * @returns Objeto com ranking (Studio) e leaderboard (API)
 */
export async function createFullRanking(
  payload: any
): Promise<{ ranking: Ranking; leaderboard: LeaderboardResponse }> {
  try {
    const ranking = await createRanking(payload);
    const operationType = payload.metric === "tasks" ? 1 : 3;

    const leaderboardData: LeaderboardPayload = {
      title: payload.title,
      description: payload.title,
      principalType: 0,
      operation: {
        type: operationType,
        achievement_type: 0,
        item: mapMetricToLeaderboard(
          payload?.metric || "points",
          payload?.actionType,
          payload?.pointType
        ),
        filters: [],
        sort: -1,
        sub: false,
      },
      period: mapPeriodToLeaderboard(payload?.period || "semanal"),
      principals: payload?.participants || [],
      techniques: ["GT03"],
      extra: ranking,
    };

    const leaderboardResponse = await api.post("/leaderboard", leaderboardData);
    const leaderboard = leaderboardResponse.data;

    clearLeaderboardsCache();
    return { ranking, leaderboard };
  } catch (error: any) {
    throw error;
  }
}

/**
 * Lista todos os leaderboards do sistema com cache de 5 minutos
 * GET /v3/leaderboard
 * @returns Array de LeaderboardResponse
 */
export async function getAllLeaderboards(): Promise<LeaderboardResponse[]> {
  try {
    const now = Date.now();
    if (
      leaderboardsCache.data &&
      now - leaderboardsCache.timestamp < CACHE_DURATION
    ) {
      return leaderboardsCache.data;
    }

    const response = await api.get("/leaderboard", {
      headers: { Range: "items=0-100" },
    });

    const data = response.data || [];

    leaderboardsCache = {
      data,
      timestamp: now,
    };

    return data;
  } catch (error: any) {
    return [];
  }
}

const pendingPlayerLeaderboardRequests = new Map<
  string,
  Promise<LeaderboardResponse[]>
>();

const playerLeaderboardsCache = new Map<
  string,
  { data: LeaderboardResponse[]; timestamp: number }
>();
const PLAYER_LEADERBOARDS_CACHE_TTL = 5 * 60 * 1000;

export async function getLeaderboardsByPlayer(
  userId: string,
  userEmail?: string
): Promise<LeaderboardResponse[]> {
  const cacheKey = `${userId}-${userEmail || "no-email"}`;

  const cached = playerLeaderboardsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < PLAYER_LEADERBOARDS_CACHE_TTL) {
    return cached.data;
  }

  const pending = pendingPlayerLeaderboardRequests.get(cacheKey);
  if (pending) {
    return pending;
  }

  const requestPromise = (async () => {
    try {
      const allLeaderboards = await getAllLeaderboards();

      const userLeaderboards = allLeaderboards.filter((lb) => {
        if (!lb.principals || !Array.isArray(lb.principals)) {
          return false;
        }

        return (
          lb.principals.includes(userId) ||
          (userEmail && lb.principals.includes(userEmail))
        );
      });

      playerLeaderboardsCache.set(cacheKey, {
        data: userLeaderboards,
        timestamp: Date.now(),
      });

      return userLeaderboards;
    } catch (error: any) {
      return [];
    } finally {
      pendingPlayerLeaderboardRequests.delete(cacheKey);
    }
  })();

  pendingPlayerLeaderboardRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

const pendingLeaderRequests = new Map<string, Promise<any[]>>();

const leadersCache = new Map<string, { data: any[]; timestamp: number }>();
const LEADERS_CACHE_TTL = 5 * 60 * 1000;

export async function getLeaderboardLeaders(
  leaderboardId: string,
  period?: string,
  live = true
): Promise<any[]> {
  const cacheKey = `${leaderboardId}-${period || "all"}-${live}`;

  const cached = leadersCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < LEADERS_CACHE_TTL) {
    return cached.data;
  }

  const pending = pendingLeaderRequests.get(cacheKey);
  if (pending) {
    return pending;
  }

  const requestPromise = (async () => {
    try {
      const params: any = { live };
      if (period) {
        params.period = period;
      }

      const response = await api.post(
        `/leaderboard/${leaderboardId}/leader/aggregate`,
        [],
        { params }
      );

      const data = Array.isArray(response.data) ? response.data : [];

      leadersCache.set(cacheKey, { data, timestamp: Date.now() });

      return data;
    } catch (error: any) {
      return [];
    } finally {
      pendingLeaderRequests.delete(cacheKey);
    }
  })();

  pendingLeaderRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Busca a posição e pontos do jogador em um leaderboard específico
 * @param leaderboardId - ID do leaderboard
 * @param playerId - ID do jogador (email ou _id)
 */
export async function getPlayerPositionInLeaderboard(
  leaderboardId: string,
  playerId: string
): Promise<{
  position: number;
  points: number;
  leaderboardTitle: string;
} | null> {
  try {
    const leaders = await getLeaderboardLeaders(leaderboardId);
    const allLeaderboards = await getAllLeaderboards();
    const leaderboard = allLeaderboards.find((lb) => lb._id === leaderboardId);

    if (!leaders || leaders.length === 0) {
      return null;
    }

    const playerData = leaders.find(
      (leader) => leader.player === playerId || leader._id === playerId
    );

    if (!playerData) {
      return null;
    }

    return {
      position: playerData.position || 0,
      points: playerData.total || 0,
      leaderboardTitle: leaderboard?.title || "Ranking",
    };
  } catch (error: any) {
    return null;
  }
}

/**
 * Interface para dados de progresso do jogador
 */
export interface PlayerProgressData {
  date: string;
  value: number;
  rank: number;
}

/**
 * Gera dados de progresso simulados baseados nos pontos atuais
 * Em produção, esta função deve ser substituída por dados reais da API
 * @param currentPoints - Pontos atuais do jogador
 * @param currentRank - Posição atual do jogador
 */
export function generateProgressData(
  currentPoints: number,
  currentRank: number
): PlayerProgressData[] {
  const today = new Date();
  const progressData: PlayerProgressData[] = [];

  // Gera 5 pontos de dados: 4 semanas anteriores + hoje
  for (let i = 4; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i * 7);

    // Calcula pontos proporcionais ao progresso
    const pointsRatio = i === 0 ? 1 : (5 - i) / 5;
    const points = Math.floor(
      currentPoints * pointsRatio * (0.8 + Math.random() * 0.2)
    );

    // Calcula rank baseado nos pontos (quanto mais pontos, melhor a posição)
    const rankVariation = Math.floor((4 - i) * 2);
    const rank =
      i === 0 ? currentRank : Math.min(currentRank + rankVariation, 50);

    const dateStr =
      i === 0
        ? "Hoje"
        : `${String(date.getDate()).padStart(2, "0")}/${String(
            date.getMonth() + 1
          ).padStart(2, "0")}`;

    progressData.push({
      date: dateStr,
      value: Math.max(points, 0),
      rank: Math.max(rank, 1),
    });
  }

  return progressData;
}
